import { morph, MORPH_OPTIONS } from '@theme/morph';

const LINK_SELECTOR = '.variant-option--colors__cell, .color-drawer__row-link';
const prefetchCache = new Map();
const preloadedImages = new Set();
/** @type {AbortController | null} */
let activeController = null;

/**
 * @param {HTMLAnchorElement} link
 * @returns {string | null}
 */
function buildTargetUrl(link) {
  const productUrl = link.dataset.productUrl || link.getAttribute('href');
  if (!productUrl) return null;

  /** @type {HTMLInputElement | null} */
  const sizeRadio = document.querySelector('.variant-picker--sizes fieldset input:checked');
  if (!sizeRadio?.value) return productUrl;

  const mapEl = document.getElementById('color-variant-map');
  if (!mapEl) return productUrl;

  try {
    const map = JSON.parse(mapEl.textContent || '{}');
    const variantId = map[productUrl]?.[sizeRadio.value];
    return variantId ? productUrl + '?variant=' + variantId : productUrl;
  } catch {
    return productUrl;
  }
}

/**
 * @param {string} html
 */
function preloadImagesFromHtml(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const imgs = /** @type {NodeListOf<HTMLImageElement>} */ (
    doc.querySelectorAll('main .product-media__image, main [class*="product-media"] img')
  );
  for (const img of imgs) {
    const src = img.currentSrc || img.getAttribute('src') || img.srcset?.split(',')[0]?.trim().split(' ')[0];
    if (src && !preloadedImages.has(src)) {
      preloadedImages.add(src);
      const preload = new Image();
      preload.src = src;
    }
  }
}

/**
 * @param {string} url
 */
function prefetch(url) {
  if (prefetchCache.has(url)) return;
  const promise = fetch(url)
    .then((r) => r.text())
    .then((html) => {
      if (html) preloadImagesFromHtml(html);
      return html;
    })
    .catch(() => null);
  prefetchCache.set(url, promise);
}

function forceLoadImages() {
  const mediaSection = document.querySelector('main');
  if (!mediaSection) return;
  const lazyImgs = mediaSection.querySelectorAll('img[loading="lazy"]');
  for (const img of lazyImgs) {
    img.removeAttribute('loading');
  }
}

document.addEventListener(
  'pointerenter',
  (e) => {
    if (!(e.target instanceof Element)) return;
    const link = /** @type {HTMLAnchorElement | null} */ (e.target.closest(LINK_SELECTOR));
    if (!link) return;
    if (link.getAttribute('aria-current') === 'true') return;
    const url = buildTargetUrl(link);
    if (url) prefetch(url);
  },
  true
);

document.addEventListener('click', async (e) => {
  if (!(e.target instanceof Element)) return;
  const link = /** @type {HTMLAnchorElement | null} */ (e.target.closest(LINK_SELECTOR));
  if (!link?.href) return;
  if (link.getAttribute('aria-current') === 'true') return;

  e.preventDefault();

  const url = buildTargetUrl(link);
  if (!url) return;

  activeController?.abort();
  activeController = new AbortController();

  /** @type {HTMLDialogElement | null} */
  const openDialog = document.querySelector('.color-drawer__dialog[open]');
  if (openDialog) openDialog.close();

  try {
    const html = prefetchCache.has(url)
      ? await prefetchCache.get(url)
      : await fetch(url, { signal: activeController.signal }).then((r) => r.text());

    if (!html) throw new Error('Empty response');

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const newMain = doc.querySelector('main');
    const currentMain = document.querySelector('main');

    if (newMain && currentMain) {
      newMain.querySelectorAll('img[loading="lazy"]').forEach((img) => img.removeAttribute('loading'));
      morph(currentMain, newMain, { ...MORPH_OPTIONS, childrenOnly: false });
      history.pushState({}, '', url);
      document.body.classList.remove('scroll-lock');
      forceLoadImages();
    } else {
      window.location.href = url;
    }
  } catch (err) {
    if (err instanceof Error && err.name !== 'AbortError') {
      window.location.href = url;
    }
  }
});

window.addEventListener('popstate', async () => {
  try {
    const html = await fetch(window.location.href).then((r) => r.text());
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const newMain = doc.querySelector('main');
    const currentMain = document.querySelector('main');
    if (newMain && currentMain) {
      newMain.querySelectorAll('img[loading="lazy"]').forEach((img) => img.removeAttribute('loading'));
      morph(currentMain, newMain, { ...MORPH_OPTIONS, childrenOnly: false });
      forceLoadImages();
    }
  } catch {
    window.location.reload();
  }
});
